import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import WhatsAppButton from "./WhatsAppButton";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://bum-desa-warga-dekat-bandung-production.up.railway.app";

const API_BASE = `${API_BASE_URL}/api/products`;

const initialForm = {
  name: "",
  price: "",
  description: "",
  stock: "",
};

function formatPrice(price) {
  return `Rp ${Number(price || 0).toLocaleString("id-ID")}`;
}

export default function AdminProducts() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const token = useMemo(() => {
    return (
      localStorage.getItem("token") || localStorage.getItem("adminToken") || ""
    );
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    navigate("/admin/login", { replace: true });
  };

  const getAuthHeaders = () => {
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const getProducts = async () => {
    try {
      setFetching(true);
      setError("");
      setMessage("");

      const res = await fetch(API_BASE, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const data = await res.json();
      console.log("GET PRODUCTS RESPONSE:", data);

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Token tidak valid / sesi admin tidak dikenali API");
        }

        throw new Error(
          data.message || data.error || "Gagal mengambil data produk",
        );
      }

      if (Array.isArray(data)) {
        setProducts(data);
      } else if (Array.isArray(data.products)) {
        setProducts(data.products);
      } else if (Array.isArray(data.data)) {
        setProducts(data.data);
      } else {
        setProducts([]);
      }
    } catch (err) {
      setError(err.message || "Terjadi kesalahan saat mengambil produk");
      setProducts([]);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    getProducts();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setError("");
    setMessage("");
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    setForm({
      name: product.name || "",
      price: product.price ?? "",
      description: product.description || "",
      stock: product.stock ?? "",
    });

    setError("");
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!form.name.trim() || form.price === "") {
      setError("Nama produk dan harga wajib diisi.");
      return;
    }

    if (Number(form.price) < 0) {
      setError("Harga tidak boleh kurang dari 0.");
      return;
    }

    if (form.stock !== "" && Number(form.stock) < 0) {
      setError("Stok tidak boleh kurang dari 0.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: form.name.trim(),
        price: Number(form.price),
        description: form.description.trim() || null,
        stock: form.stock === "" ? 0 : Number(form.stock),
      };

      const url = editingId ? `${API_BASE}/${editingId}` : API_BASE;
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      console.log("SAVE PRODUCT RESPONSE:", data);

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Token tidak valid / sesi admin tidak dikenali API");
        }

        throw new Error(data.message || data.error || "Gagal menyimpan produk");
      }

      setMessage(
        editingId
          ? "Produk berhasil diperbarui."
          : "Produk berhasil ditambahkan.",
      );
      resetForm();
      getProducts();
    } catch (err) {
      setError(err.message || "Terjadi kesalahan saat menyimpan produk");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Yakin ingin menghapus produk ini?");
    if (!ok) return;

    setError("");
    setMessage("");

    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      console.log("DELETE PRODUCT RESPONSE:", data);

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Token tidak valid / sesi admin tidak dikenali API");
        }

        throw new Error(data.message || data.error || "Gagal menghapus produk");
      }

      setMessage("Produk berhasil dihapus.");
      if (editingId === id) resetForm();
      getProducts();
    } catch (err) {
      setError(err.message || "Terjadi kesalahan saat menghapus produk");
    }
  };

  return (
    <div className="min-h-screen bg-[#f8faf8] font-serif">
      <Navbar />
      <WhatsAppButton />

      <section className="pt-32 pb-12 px-6 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-3">
              Dashboard Admin
            </p>
            <h1 className="text-4xl text-[#3d4f45] font-light mb-3">
              Kelola Produk
            </h1>
            <p className="text-sm text-gray-500 max-w-2xl">
              Tambah, edit, hapus, dan lihat data produk dari dashboard admin.
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <NavLink
                to="/admin/posts"
                className={({ isActive }) =>
                  `px-4 py-2 rounded-xl text-sm transition border ${
                    isActive
                      ? "bg-[#3d4f45] text-white border-[#3d4f45]"
                      : "border-gray-300 text-[#3d4f45] hover:bg-[#f1f5f2]"
                  }`
                }
              >
                Kelola Berita
              </NavLink>

              <NavLink
                to="/admin/products"
                className={({ isActive }) =>
                  `px-4 py-2 rounded-xl text-sm transition border ${
                    isActive
                      ? "bg-[#3d4f45] text-white border-[#3d4f45]"
                      : "border-gray-300 text-[#3d4f45] hover:bg-[#f1f5f2]"
                  }`
                }
              >
                Kelola Produk
              </NavLink>
            </div>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className="h-fit rounded-xl border border-red-300 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition"
          >
            Logout
          </button>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_1.3fr] gap-8">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 h-fit">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl text-[#3d4f45] font-light">
                {editingId ? "Edit Produk" : "Tambah Produk"}
              </h2>

              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-sm text-gray-500 hover:text-[#3d4f45]"
                >
                  Batal Edit
                </button>
              )}
            </div>

            {message && (
              <div className="mb-4 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
                {message}
              </div>
            )}

            {error && (
              <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-[#3d4f45] mb-2">
                  Nama Produk
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Masukkan nama produk"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#3d4f45]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-[#3d4f45] mb-2">
                  Harga
                </label>
                <input
                  type="number"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  placeholder="Masukkan harga"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#3d4f45]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-[#3d4f45] mb-2">
                  Deskripsi
                </label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Masukkan deskripsi produk"
                  rows="5"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#3d4f45]"
                />
              </div>

              <div>
                <label className="block text-sm text-[#3d4f45] mb-2">
                  Stok
                </label>
                <input
                  type="number"
                  name="stock"
                  value={form.stock}
                  onChange={handleChange}
                  placeholder="Masukkan stok"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#3d4f45]"
                />
              </div>

              <div className="pt-3 flex gap-3 flex-wrap">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#3d4f45] text-white px-6 py-3 rounded-xl text-sm hover:bg-[#4d6156] transition disabled:opacity-50"
                >
                  {loading
                    ? "Menyimpan..."
                    : editingId
                      ? "Simpan Perubahan"
                      : "Tambah Produk"}
                </button>

                <button
                  type="button"
                  onClick={resetForm}
                  className="border border-gray-300 text-gray-600 px-6 py-3 rounded-xl text-sm hover:bg-gray-50 transition"
                >
                  Reset
                </button>
              </div>
            </form>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6 gap-4">
              <div>
                <h2 className="text-2xl text-[#3d4f45] font-light mb-2">
                  Daftar Produk
                </h2>
                <p className="text-sm text-gray-500">
                  Kelola semua produk yang tersedia.
                </p>
              </div>

              <button
                onClick={getProducts}
                className="bg-[#3d4f45] text-white px-5 py-3 rounded-xl text-sm hover:bg-[#4d6156] transition"
              >
                Refresh
              </button>
            </div>

            {fetching ? (
              <p className="text-gray-500">Sedang memuat data produk...</p>
            ) : products.length === 0 ? (
              <p className="text-gray-500">Belum ada produk.</p>
            ) : (
              <div className="space-y-4">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="border border-gray-200 rounded-2xl p-5 hover:shadow-sm transition"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg text-[#3d4f45] font-semibold mb-2">
                          {product.name}
                        </h3>

                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-semibold">Harga:</span>{" "}
                          {formatPrice(product.price)}
                        </p>

                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-semibold">Stok:</span>{" "}
                          {product.stock ?? 0}
                        </p>

                        <p className="text-sm text-gray-600 leading-7">
                          {product.description || "Tidak ada deskripsi."}
                        </p>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => handleEdit(product)}
                          className="px-4 py-2 rounded-xl border border-[#3d4f45] text-[#3d4f45] text-sm hover:bg-[#3d4f45] hover:text-white transition"
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDelete(product.id)}
                          className="px-4 py-2 rounded-xl border border-red-300 text-red-600 text-sm hover:bg-red-50 transition"
                        >
                          Hapus
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
